import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import TabBar from '../../components/TabBar';
import { Wallet, ArrowLeft, CheckCircle2, RefreshCw, XCircle, AlertTriangle, Users as UsersIcon, Briefcase, Calculator, Plus } from 'lucide-react';
import api from '../../utils/api';
import { beijingToday, formatBJTime } from '../../utils/datetime';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import { fmtMoney as fmt } from '../../utils/clipboard';

// 结算管理页（分视角）
// 管理员：只显示 ①代理商汇总（一次性结算其名下完成量，单价默认=基准价） ②直属/无归属用户；
//         不再显示代理名下普通用户、不再显示外部接单人结算区。
// 代理商：只显示自己名下用户（含自己代提的外部档案），给下级结算。
export default function SettlementPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { subscribe } = useSocket();
  const { showToast } = useToast();
  const [users, setUsers] = useState([]);          // 代理视角：名下用户；管理员：直属用户
  const [agents, setAgents] = useState([]);        // 管理员视角：代理商汇总（含外部档案/自提完成量）
  const [records, setRecords] = useState([]);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const [settleCount, setSettleCount] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);
  // 证券：同一个结算对象的排五 + 证券在一个弹窗里一次结算
  const [puzzleOn, setPuzzleOn] = useState(false);        // 本次是否连同证券一起结算
  const [puzzleCountInput, setPuzzleCountInput] = useState('');   // 证券本次结算数量（默认 = 全部待结算，可手动改小）
  // 首次设置单价（价格还没配置时才出现；配置过之后就只读，再改要去「用户管理」）
  const [firstPrice, setFirstPrice] = useState({ legacy: '', puzzle: '' });
  const [savingPriceBiz, setSavingPriceBiz] = useState('');
  const today = beijingToday();
  const [selectedDate, setSelectedDate] = useState(today);

  // 金额：排五（数量 × 单价，数量可手动改）+ 证券（待结算单数 × 单价，单数以服务器为准）
  // 单价不在这里改：统一在「用户管理」里配置（代理商 = 结算价，普通用户 = 单价），这里只读显示，服务端结算时也以配置为准。
  const legacyCount   = parseInt(settleCount) || 0;
  const legacyPrice   = selectedUser ? (Number(selectedUser.isAgent ? selectedUser.basePrice : selectedUser.price) || 0) : 0;
  const legacyTotal   = legacyCount * legacyPrice;
  const pzPending     = selectedUser?.puzzle?.pending_count || 0;
  const puzzlePrice   = Number(selectedUser?.puzzle?.price) || 0;
  const pzCount       = parseInt(puzzleCountInput) || 0;                 // 证券本次要结的数量（可手动改大改小，不设上限）
  const puzzleActive  = puzzleOn && pzCount > 0 && puzzlePrice > 0;      // 本次是否真的带上证券
  const puzzleTotal   = puzzleActive ? pzCount * puzzlePrice : 0;
  const totalAmount   = legacyTotal + puzzleTotal;

  const fetchAll = useCallback(async (date) => {
    const d = date || selectedDate || today;
    const [ap, r] = await Promise.all([
      api.get('/settlement/admin-pending', { params: { date: d } }).catch(() => ({ data: { agents: [], directs: [] } })),
      api.get('/settlement/records-all', { params: { date: d } }).catch(() => ({ data: { records: [] } })),
    ]);
    setRecords(r.data.records || []);
    // agents：调用者自己直属的下级代理（管理员=顶层代理；代理=自己直属下级代理），逐层递归同一套口径
    setAgents(ap.data.agents || []);
    // directs：调用者自己名下的普通用户（管理员=直属/无归属；代理=自己名下，含自己代提的外部档案），有 pending 才展示
    const directs = (ap.data.directs || []).filter(d => d.pending_count > 0 || d.pending_batches > 0 || (d.puzzle?.pending_count || 0) > 0);
    setUsers(directs);
  }, [today, selectedDate]);

  useEffect(() => { fetchAll(selectedDate); }, [fetchAll, selectedDate]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'settlement_updated' || event === 'users_updated' || event === 'puzzle_settlement_updated' || event === 'puzzle_updated') fetchAll();
    });
  }, [subscribe, fetchAll]);

  const openSettle = async (u) => {
    setSelectedUser(u);
    setSettleCount('');
    setPendingData(null);
    const pz = u.puzzle;
    setPuzzleOn(!!pz && pz.pending_count > 0 && Number(pz.price) > 0);   // 证券有待结算、且已设置单价，就默认一起结
    setPuzzleCountInput(pz ? String(pz.pending_count || 0) : '');         // 默认全部待结算，可改小
    setFirstPrice({ legacy: '', puzzle: '' });
    setShowSettleModal(true);

    setPendingLoading(true);
    try {
      const res = await api.get(`/settlement/pending/${u.id}`);
      setPendingData(res.data);
      // 自动填入成功组数 × 单价
      if (res.data.ocrQualified > 0) {
        setSettleCount(String(res.data.ocrQualified));
      }
    } catch (err) { console.error(err); } finally { setPendingLoading(false); }
  };

  const handleSettle = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    try {
      if (puzzleActive) {
        // 排五 + 证券 一次结算：两边一起校验、一起生效，任何一边不通过整单都不结
        const res = await api.post('/settlement/combined', {
          userId: selectedUser.id,
          // 排五只在“有数量、也有单价”时才带上：数量被自动填了但没有单价（比如这个人排五没设单价）时不要写一条 0 元的排五结算
          // price 只是把页面上看到的单价带给服务端做“是否过期”核对，实际结算价以服务端读到的配置为准
          legacy: legacyCount > 0 && legacyPrice > 0 ? { count: legacyCount, price: legacyPrice } : null,
          // count = 本次要结的证券数量（手动改过就只结前 N 单，其余继续留在待结算）；expectedCount = 页面看到的待结算总数，用来防数据过期
          puzzle: { price: puzzlePrice, count: pzCount, expectedCount: pzPending },
        });
        showToast(`结算成功：合计 ¥${fmt(res.data.totalAmount)}`, 'success');
      } else {
        await api.post('/settlement', {
          userId: selectedUser.id,
          count: parseInt(settleCount) || 0,
          price: legacyPrice,
        });
        showToast('结算成功', 'success');
      }
      setShowSettleModal(false);
      fetchAll();
    } catch (err) {
      const d = err.response?.data;
      showToast(d?.error || '结算失败', 'error');
      if (d?.code === 'DATA_CHANGED' || d?.code === 'PRICE_CHANGED') { setShowSettleModal(false); fetchAll(); }   // 证券待结算数量 / 单价变了：刷新列表重新结
    } finally { setLoading(false); }
  };

  // 首次设置单价：只在这个业务的价格还没配置（= 0）时可用，走和「用户管理」同一套接口和权限校验；
  // 配置过之后弹窗里就只读，再修改要去「用户管理」。
  const saveFirstPrice = async (biz) => {
    if (!selectedUser || savingPriceBiz) return;
    const p = parseFloat(firstPrice[biz]);
    if (!Number.isFinite(p) || p <= 0) { showToast('请输入大于 0 的单价', 'error'); return; }
    setSavingPriceBiz(biz);
    try {
      if (biz === 'legacy') {
        await api.put(`/users/${selectedUser.id}/price`, { price: p });
        setSelectedUser(u => ({ ...u, ...(u.isAgent ? { basePrice: p } : { price: p }) }));
      } else {
        await api.put(`/puzzle/price/${selectedUser.id}`, { price: p });
        setSelectedUser(u => ({ ...u, puzzle: { ...(u.puzzle || {}), price: p } }));
        if (pzPending > 0) setPuzzleOn(true);
      }
      showToast(`${biz === 'legacy' ? '排五' : '证券'}${selectedUser.isAgent ? '结算价' : '单价'}已设置，之后修改请到「用户管理」`, 'success');
      fetchAll();
    } catch (err) {
      showToast(err.response?.data?.error || '设置失败', 'error');
    } finally { setSavingPriceBiz(''); }
  };

  // 今日结算记录按用户分组
  const recordsByUser = records.reduce((acc, r) => {
    if (!acc[r.user_id]) acc[r.user_id] = [];
    acc[r.user_id].push(r);
    return acc;
  }, {});

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Wallet size={16} /> 结算管理
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AdminNotificationBell />
          <button
            onClick={() => navigate(isAdmin ? '/admin/settlement-overview' : '/admin/agent-settlement-overview')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--c-primary)', fontWeight: 600, padding: '4px 0' }}>
            统计
          </button>
        </div>
      </div>
      {/* 日期选择器 */}
      <div style={{ padding: '10px 16px 8px', borderBottom: '0.5px solid var(--c-border)', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--c-surface, #fff)' }}>
        <span style={{ fontSize: 13, color: 'var(--c-text-secondary)', flexShrink: 0 }}>结算日期</span>
        <input className="input" type="date" value={selectedDate}
          onChange={e => { setSelectedDate(e.target.value); fetchAll(e.target.value); }}
          style={{ flex: 1 }} />
        {selectedDate !== today && (
          <button className="btn btn-ghost btn-sm"
            style={{ fontSize: 12, whiteSpace: 'nowrap', padding: '4px 10px' }}
            onClick={() => { setSelectedDate(today); fetchAll(today); }}>
            回今日
          </button>
        )}
      </div>

      <div style={{ padding: '12px 16px' }}>

        {/* ═══ 管理员视角：代理商汇总 ═══ */}
        {agents.length > 0 && (
          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Briefcase size={15} /> 代理商结算
            </h3>
            {agents.map(a => (
              <div key={a.id} className="data-row">
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {a.name || a.phone || `代理${a.id}`}
                    <span className="badge badge-info" style={{ marginLeft: 6, fontSize: 11 }}>代理</span>
                    {(a.agentPending || (a.puzzle?.pending_count || 0) > 0) && <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 11 }}>有未结算</span>}
                  </div>
                  <PendingLines isAgent legacy={{ qty: a.pending_qualified, price: a.basePrice }}
                    ocr={{ qualified: a.todayOcrQualified, duplicate: a.todayOcrDuplicate, invalid: a.todayOcrInvalid, failed: a.todayOcrFailed }} puzzle={a.puzzle} />
                  {recordsByUser[a.id]?.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--c-primary)', marginTop: 2 }}>
                      今日已结算 {recordsByUser[a.id].length} 次 · 共 ¥{fmt(recordsByUser[a.id].reduce((s, r) => s + r.total_amount, 0))}
                    </div>
                  )}
                </div>
                {(() => {
                  const hasSettled = recordsByUser[a.id]?.some(r => r.settlement_type === 'agent_payout');
                  return (
                    <button className={`btn btn-sm ${hasSettled ? 'btn-outline' : 'btn-primary'}`}
                      onClick={() => openSettle({ ...a, isAgent: true, name: a.name || `代理${a.id}`, phone: a.phone })}
                      style={hasSettled ? { opacity: 0.6 } : {}}>
                      {hasSettled ? '再结算' : '结算'}
                    </button>
                  );
                })()}
              </div>
            ))}
          </div>
        )}

        {/* ═══ 待结算（管理员=直属用户；代理=名下用户） ═══ */}
        <div className="card">
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <UsersIcon size={15} /> {isAdmin ? '直属用户待结算' : '名下用户待结算'}
          </h3>
          {users.length === 0 ? (
            <p style={{ textAlign: 'center', padding: 20, color: 'var(--c-text-secondary)', fontSize: 14 }}>
              暂无待结算用户
            </p>
          ) : users.map(u => (
            <div key={u.id} className="data-row">
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {u.name || u.phone}
                  {u.is_external ? <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 11 }}>外部</span> : null}
                </div>
                <PendingLines
                  legacy={{ qty: u.pending_qualified, price: u.price }}
                  ocr={{ qualified: u.todayOcrQualified, duplicate: u.todayOcrDuplicate, invalid: u.todayOcrInvalid, failed: u.todayOcrFailed }}
                  puzzle={u.puzzle} />
                {recordsByUser[u.id]?.length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--c-primary)', marginTop: 2 }}>
                    今日已结算 {recordsByUser[u.id].length} 次 · 共 ¥{fmt(recordsByUser[u.id].reduce((s, r) => s + r.total_amount, 0))}
                  </div>
                )}
              </div>
              {(() => {
                  const hasSettled = recordsByUser[u.id]?.length > 0;
                  return (
                    <button className={`btn btn-sm ${hasSettled ? 'btn-outline' : 'btn-primary'}`}
                      onClick={() => openSettle(u)}
                      style={hasSettled ? { opacity: 0.6 } : {}}>
                      {hasSettled ? '再结算' : '结算'}
                    </button>
                  );
                })()}
            </div>
          ))}
        </div>

        {/* 今日结算记录 */}
        {records.length > 0 && (
          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>{selectedDate === today ? '今日' : selectedDate} 结算记录</h3>
            {records.map(r => (
              <div key={`${r.business || 'legacy'}-${r.id}`} className="data-row">
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <BizTag biz={r.business} />
                    {r.user_name || r.user_phone}
                    {r.settlement_type === 'agent_payout' && <span className="badge badge-info" style={{ marginLeft: 6, fontSize: 11 }}>付代理商</span>}
                    {r.ext_user_name && <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 11 }}>外部·{r.ext_user_name}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                    {r.count || 0} 单{r.price != null && r.price > 0 ? ` × ¥${r.price}` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: 'var(--c-success)', fontSize: 15 }}>¥{fmt(r.total_amount)}</div>
                  <span className="badge badge-success" style={{ fontSize: 11 }}>已结算</span>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', borderTop: '0.5px solid var(--c-border)', marginTop: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)' }}>今日合计</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--c-success)' }}>
                ¥{fmt(records.reduce((s, r) => s + r.total_amount, 0))}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ===== 结算填写弹窗 ===== */}
      <Modal show={showSettleModal} onClose={() => setShowSettleModal(false)}
        title={`结算 - ${selectedUser?.name || selectedUser?.phone}`}>

        {/* 排五：和下面的证券是同一套卡片（色块 + 标签 + 标题行 → 数量 → 单价 → 小计），只是颜色不同（排五蓝、证券紫）。
            数量留空 = 这次不结排五；单价只读（首次没配置时可在这里设一次）。 */}
        <div style={{ border: '1px solid rgba(0,122,255,0.25)', background: 'rgba(0,122,255,0.05)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <BizTag biz="legacy" /><span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>排五{pendingData ? ` · ${pendingData.batches.length} 批` : ''}</span>
            {selectedUser?.puzzle?.pending_count > 0 && <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>留空 = 这次不结排五</span>}
          </div>

          {/* 参考信息：压成一行小字，不再单独占一块灰底 */}
          {pendingLoading ? (
            <p style={{ fontSize: 12, color: 'var(--c-text-secondary)', margin: '0 0 10px' }}>加载待结算数据...</p>
          ) : pendingData ? (
            <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', lineHeight: 1.7, marginBottom: 10 }}>
              今日总派发 {pendingData.totalDispatched} 条（参考）
              {pendingData.settledCount > 0 && <>{' · '}已结算 {pendingData.settledCount} 单（今日全量 OCR {pendingData.ocrQualifiedTotal} 组）</>}
              {pendingData.lastSettlementAt && <>{' · '}上次结算 {formatBJTime(pendingData.lastSettlementAt)}</>}
              {!pendingData.hasPending && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--c-accent)', fontSize: 12, marginTop: 4 }}>
                  <AlertTriangle size={13} />
                  <span>暂无新完成任务（可按实际新增量手动填写）</span>
                </div>
              )}
            </div>
          ) : null}

          {/* 快捷填入数量（仅 pendingData 有 OCR 数据时显示） */}
          {pendingData && (pendingData.ocrQualified > 0 || pendingData.ocrInvalid > 0) && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Calculator size={13} color="var(--c-text-secondary)" />
                <span style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>快捷填入数量</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* 默认：仅成功（已自动填入，此按钮用于重置） */}
                {pendingData.ocrQualified > 0 && (
                  <button className="btn btn-outline btn-sm"
                    onClick={() => setSettleCount(String(pendingData.ocrQualified))}
                    style={{ fontSize: 12, padding: '4px 10px', height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CheckCircle2 size={12} strokeWidth={2.5} color="var(--c-success)" />
                    仅成功 {pendingData.ocrQualified} 组
                  </button>
                )}
                {/* 特殊情况：含无效一起算 */}
                {pendingData.ocrInvalid > 0 && (
                  <button className="btn btn-outline btn-sm"
                    onClick={() => setSettleCount(String(pendingData.ocrQualified + pendingData.ocrInvalid))}
                    style={{ fontSize: 12, padding: '4px 10px', height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Plus size={12} strokeWidth={2.5} />
                    含无效 {pendingData.ocrQualified + pendingData.ocrInvalid} 组
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 数量（可改）+ 单价（只读，统一在用户管理里配置） */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 4, display: 'block' }}>数量</label>
            <input className="input" type="number" step="1" min="0"
              value={settleCount} onChange={e => setSettleCount(e.target.value)} placeholder="0" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
            <span style={{ color: 'var(--c-text-secondary)', fontWeight: 600 }}>{selectedUser?.isAgent ? '结算价' : '单价'}（元/单）</span>
            {legacyPrice > 0
              ? <strong>¥{fmt(legacyPrice)}</strong>
              : <span style={{ color: 'var(--c-danger)' }}>未设置</span>}
          </div>
          {legacyPrice <= 0 && (
            <FirstPriceRow value={firstPrice.legacy} onChange={v => setFirstPrice(f => ({ ...f, legacy: v }))}
              onSave={() => saveFirstPrice('legacy')} saving={savingPriceBiz === 'legacy'} />
          )}
          {legacyCount > 0 && legacyPrice > 0 && (
            <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 8, lineHeight: 1.6 }}>
              排五 {legacyCount} 单 × ¥{fmt(legacyPrice)} = ¥{fmt(legacyTotal)}
            </div>
          )}
        </div>

        {/* 证券：和排五一起结。数量默认 = 系统待结算的全部，可手动改大改小（不设上限）；单价只读（首次没配置时可在这里设一次） */}
        {selectedUser?.puzzle && (
          <div style={{ border: '1px solid rgba(175,82,222,0.25)', background: 'rgba(175,82,222,0.05)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <BizTag biz="puzzle" /><span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>证券 · 待结算 {pzPending} 单</span>
              {puzzlePrice > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={puzzleOn} onChange={e => setPuzzleOn(e.target.checked)} /> 一起结算
                </label>
              )}
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 4, display: 'block' }}>数量</label>
              <input className="input" type="number" step="1" min="0"
                value={puzzleCountInput} onChange={e => setPuzzleCountInput(e.target.value)} placeholder="0"
                disabled={!puzzleOn && puzzlePrice > 0} />
              {/* 只是提示，不限制：识别出错没来得及修正时，系统记录会比实际成功数少 */}
              {pzCount > pzPending && (
                <div style={{ fontSize: 11, color: 'var(--c-accent)', marginTop: 4 }}>比系统待结算的 {pzPending} 单多 {pzCount - pzPending} 单，按填的数量结算</div>
              )}
              {pzCount > 0 && pzCount < pzPending && (
                <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 4 }}>本次只结 {pzCount} 单，剩下 {pzPending - pzCount} 单继续留在待结算</div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
              <span style={{ color: 'var(--c-text-secondary)', fontWeight: 600 }}>{selectedUser?.isAgent ? '结算价' : '单价'}（元/单）</span>
              {puzzlePrice > 0
                ? <strong>¥{fmt(puzzlePrice)}</strong>
                : <span style={{ color: 'var(--c-danger)' }}>未设置</span>}
            </div>
            {puzzlePrice <= 0 && (
              <FirstPriceRow value={firstPrice.puzzle} onChange={v => setFirstPrice(f => ({ ...f, puzzle: v }))}
                onSave={() => saveFirstPrice('puzzle')} saving={savingPriceBiz === 'puzzle'} />
            )}
            {puzzleActive && (
              <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 8, lineHeight: 1.6 }}>
                证券 {pzCount} 单 × ¥{fmt(puzzlePrice)} = ¥{fmt(puzzleTotal)}；系统待结算数量中途有变化会提示刷新。
              </div>
            )}
          </div>
        )}

        {/* 金额预览 */}
        <div style={{ background: 'var(--c-primary-light)', padding: '14px 16px', borderRadius: 10, marginBottom: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 4 }}>本次结算金额{puzzleActive && legacyCount > 0 ? '（排五 + 证券）' : ''}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: totalAmount > 0 ? 'var(--c-primary)' : 'var(--c-text-secondary)' }}>
            ¥{fmt(totalAmount)}
          </div>
          {puzzleActive && legacyCount > 0 && (
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 6 }}>
              排五 ¥{fmt(legacyTotal)} + 证券 ¥{fmt(puzzleTotal)}
            </div>
          )}
        </div>

        <button className="btn btn-primary" style={{ width: '100%', height: 48, fontSize: 15 }}
          onClick={() => totalAmount > 0 && setShowConfirmModal(true)}
          disabled={loading || totalAmount === 0}>
          {loading ? '结算中...' : '确认结算'}
        </button>
      </Modal>

      {/* ===== 二次确认弹窗 ===== */}
      <Modal show={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="确认结算">
        <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
          <CheckCircle2 size={48} color="var(--c-success)" style={{ margin: '0 auto 12px', display: 'block' }} />
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
            为 {selectedUser?.name || selectedUser?.phone} 结算
          </div>
          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 4, lineHeight: 1.8 }}>
            {legacyCount > 0 && <div><BizTag biz="legacy" />{legacyCount} 单 × ¥{fmt(legacyPrice)} = ¥{fmt(legacyTotal)}</div>}
            {puzzleActive && <div><BizTag biz="puzzle" />{pzCount} 单 × ¥{fmt(puzzlePrice)} = ¥{fmt(puzzleTotal)}</div>}
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--c-success)', margin: '12px 0 20px' }}>
            ¥{fmt(totalAmount)}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-outline" style={{ flex: 1, height: 46 }}
              onClick={() => setShowConfirmModal(false)}>取消</button>
            <button className="btn btn-primary" style={{ flex: 1, height: 46, fontSize: 15 }}
              onClick={handleSettle} disabled={loading}>
              {loading ? '结算中...' : '确认'}
            </button>
          </div>
        </div>
      </Modal>

      <TabBar mode="admin" />
    </div>
  );
}

// 首次设置单价的输入行：只在价格还没配置时显示；保存后变成只读，再修改要去「用户管理」
function FirstPriceRow({ value, onChange, onSave, saving }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="input" type="number" step="0.01" min="0" inputMode="decimal" value={value}
          onChange={e => onChange(e.target.value)} placeholder="首次设置，元/单" style={{ flex: 1 }} />
        <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving || !(parseFloat(value) > 0)} style={{ flexShrink: 0 }}>
          {saving ? '保存中' : '保存单价'}
        </button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 4 }}>只有首次可以在这里设置；设置后再要修改，请到「用户管理」。</div>
    </div>
  );
}

// 业务标签（排五 / 证券），和二次审查、号码溯源里的一致
function BizTag({ biz }) {
  const puzzle = biz === 'puzzle';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, borderRadius: 5, padding: '1px 6px', marginRight: 6, flexShrink: 0,
      background: puzzle ? 'rgba(175,82,222,0.14)' : 'rgba(0,122,255,0.12)',
      color: puzzle ? '#8944ab' : 'var(--c-primary)',
    }}>{puzzle ? '证券' : '排五'}</span>
  );
}

// 结算列表里每一行的“两个业务能结多少”：每个业务一行，写成 数量 × 单价 = 金额，最后给一个预计合计。
//   排五：今日成功 N 组（就是点“结算”后弹窗自动填入的数量）
//   证券：待结成功 M 单（已计入、还没结算的成功单，就是这次会结的单数）
// 每个业务的下面各有一行“今日状态数量”小计（不再显示“提取 N 条”这种仅供参考的数字）：
//   排五：成功 / 重复 / 无效 / 失败（无效和失败分开：无效＝识别到号码但配对没通过，失败＝根本没识别出号码）
//   证券：已计入 / 待核实 / 已作废
// 没设单价的业务只显示数量，不计入预计合计。
function StatusChips({ items }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 9px', margin: '2px 0 0 42px', fontSize: 11 }}>
      {items.map(([label, n, color, Icon, unit]) => (
        <span key={label} style={{ color: n > 0 ? color : 'var(--c-text-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <Icon size={11} strokeWidth={2.4} />{label} {n}{unit ? ` ${unit}` : ''}
        </span>
      ))}
    </div>
  );
}
function PendingLines({ legacy, puzzle, ocr, isAgent }) {
  const lq = (legacy && legacy.qty) || 0;
  const lp = Number(legacy && legacy.price) || 0;
  const zq = (puzzle && puzzle.pending_count) || 0;
  const zp = Number(puzzle && puzzle.price) || 0;
  const lAmt = lq * lp, zAmt = zq * zp;
  const total = lAmt + zAmt;
  const priceWord = isAgent ? '结算价' : '单价';
  const o = ocr || {};
  const t = (puzzle && puzzle.today) || {};
  const line = (biz, label, qty, unit, price, amt) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4, fontSize: 12.5 }}>
      <span style={{ color: 'var(--c-text-secondary)', display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <BizTag biz={biz} />
        {label} <strong style={{ color: qty > 0 ? (biz === 'puzzle' ? '#8944ab' : 'var(--c-primary)') : 'var(--c-text-tertiary)', margin: '0 3px' }}>{qty}</strong> {unit}
        {qty > 0 && price > 0 && <span style={{ marginLeft: 4 }}>× ¥{fmt(price)}</span>}
        {qty > 0 && price <= 0 && <span style={{ marginLeft: 4, color: 'var(--c-text-tertiary)' }}>· 未设置{priceWord}</span>}
      </span>
      {qty > 0 && price > 0 && <strong style={{ color: 'var(--c-text)', flexShrink: 0 }}>¥{fmt(amt)}</strong>}
    </div>
  );
  return (
    <div style={{ marginTop: 6 }}>
      {line('legacy', '今日成功', lq, '组', lp, lAmt)}
      <StatusChips items={[
        ['成功', o.qualified || 0, 'var(--c-success)', CheckCircle2, '组'],
        ['重复', o.duplicate || 0, 'var(--c-accent)', RefreshCw, '组'],
        ['无效', o.invalid || 0, 'var(--c-danger)', AlertTriangle, '组'],
        ['失败', o.failed || 0, 'var(--c-text-secondary)', XCircle, '张'],
      ]} />
      {line('puzzle', '待结成功', zq, '单', zp, zAmt)}
      <StatusChips items={[
        ['已计入', t.counted || 0, 'var(--c-success)', CheckCircle2, '单'],
        ['待核实', t.pending || 0, 'var(--c-accent)', AlertTriangle, '单'],
        ['已作废', t.rejected || 0, 'var(--c-danger)', XCircle, '单'],
      ]} />
      {total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '0.5px dashed var(--c-border)', fontSize: 12.5 }}>
          <span style={{ color: 'var(--c-text-secondary)' }}>预计合计</span>
          <strong style={{ color: 'var(--c-success)', fontSize: 14 }}>¥{fmt(total)}</strong>
        </div>
      )}
    </div>
  );
}
